/**
 * tenant-onboarding-flow D2/D3: the admin Home setup card's eight rules (seven
 * for a studio that bought a custom look: no Pick your look row).
 *
 * One pure function decides every row from a small snapshot the API builds per
 * read. The server owns facts and destinations; the webapp owns every label,
 * verb and reason line. Persistence contract: docs/data-model/dm-tenant-onboarding.md.
 *
 * Browser-safe: its only import is the help article registry (zero imports).
 */
import { ONBOARDING_HELP_ARTICLE_IDS, HELP_HOME_PATH, helpArticlePath } from '../help/articleIds.js';

/** The eight counted rows, in card order. A custom-look studio gets them minus `look`. */
export const SETUP_ROW_KEYS = [
  'hours',
  'offering',
  'form',
  'brand',
  'look',
  'refund',
  'payments',
  'learn',
] as const;
export type SetupRowKey = (typeof SETUP_ROW_KEYS)[number];

/**
 * The rows a stored opened mark can satisfy. Pick your look is not one: it is
 * done by data from a saved look (`lookChosen`, client-theme-build D6), so the
 * mark route refuses `row-opened` for `look` and a legacy stored `look` is ignored.
 */
export const SETUP_OPENED_ROWS = ['form', 'refund'] as const;
export type SetupOpenedRow = (typeof SETUP_OPENED_ROWS)[number];

/**
 * The five Learn articles, in Learn order. The help center registry owns the
 * list (typed `satisfies readonly HelpArticleId[]`), so a renamed id fails typecheck.
 */
export const SETUP_LEARN_ARTICLE_IDS = ONBOARDING_HELP_ARTICLE_IDS;
export type SetupLearnArticleId = (typeof SETUP_LEARN_ARTICLE_IDS)[number];

/** The most counted rows a card can have. The per-tenant count is the DTO's `total` (7 for a custom-look studio). */
export const SETUP_TOTAL_ROWS = 8 as const;

/** Payments reads this query to open its refund policy sheet once (D8). */
export const REFUND_POLICY_DEEP_LINK = { param: 'open', value: 'refund-policy' } as const;

/**
 * Business profile reads this query to open its Client look spoke once
 * (client-theme-build D4). The setup row's path below was set in the same task
 * as the saved-look rule (client-theme-build task 8.2, D6), never before it.
 */
export const BUSINESS_PROFILE_LOOK_DEEP_LINK = { param: 'open', value: 'look' } as const;

/** Row destinations (D3). Every pattern is asserted against `ADMIN_WEBAPP_ROUTES`. */
export const SETUP_PATHS = {
  hours: '/admin/availability',
  offering: '/admin/services/new',
  formsList: '/admin/forms',
  brand: '/admin/business-profile',
  payments: '/admin/payments',
  help: HELP_HOME_PATH,
} as const;

export function setupFormPath(registrationFormId: string | null): string {
  return registrationFormId ? `/admin/forms/${registrationFormId}` : SETUP_PATHS.formsList;
}

export const SETUP_REFUND_PATH =
  `${SETUP_PATHS.payments}?${REFUND_POLICY_DEEP_LINK.param}=${REFUND_POLICY_DEEP_LINK.value}` as const;

/** Pick your look opens Business profile with the look picker spoke open (D9, client-theme-build D4). */
export const SETUP_LOOK_PICKER_PATH =
  `${SETUP_PATHS.brand}?${BUSINESS_PROFILE_LOOK_DEEP_LINK.param}=${BUSINESS_PROFILE_LOOK_DEEP_LINK.value}` as const;

/** The stored half: the tenant's `SetupProgress` row, or all defaults when none exists. */
export interface SetupMarks {
  openedRows: readonly string[];
  openedArticles: readonly string[];
  cashOnly: boolean;
  welcomeSeen: boolean;
  collapsed: boolean;
  completed: boolean;
}

export const EMPTY_SETUP_MARKS: SetupMarks = {
  openedRows: [],
  openedArticles: [],
  cashOnly: false,
  welcomeSeen: false,
  collapsed: false,
  completed: false,
};

/** Everything the eight rules read, built by the API per request inside the tenant context. */
export interface SetupSnapshot {
  defaultPractitionerId: string | null;
  /** The tenant holds the `custom-look` feature grant (bought the custom UI package): no look row. */
  customLook: boolean;
  /**
   * An admin saved a client look (`BusinessConfig.clientLookPickedAt` is set;
   * client-theme-build). A provisioned default never counts. The only thing
   * that completes Pick your look: opening the picker never does.
   */
  lookChosen: boolean;
  /** Distinct ids of this tenant's practitioners with at least one weekly window. */
  practitionersWithHours: readonly string[];
  hasLiveService: boolean;
  /** An event template with a CONFIRMED session starting in the future. */
  hasScheduledEvent: boolean;
  /** `BusinessConfig.brandLogo` as stored. */
  brandLogo: string | null;
  /** `BusinessConfig.aboutStory` as stored (never the short `about` line). */
  aboutStory: string | null;
  /** `BusinessConfig.aboutImageUrl` as stored: the About photo is required (admin-photo-shows-on). */
  aboutImageUrl: string | null;
  stripeChargesEnabled: boolean;
  registrationFormId: string | null;
  marks: SetupMarks;
}

export type SetupDoneBy = 'data' | 'opened' | 'cash-only';

export interface SetupRowDto {
  key: SetupRowKey;
  done: boolean;
  doneBy: SetupDoneBy | null;
  /** Null only for the Learn disclosure. */
  path: string | null;
}

export interface SetupLearnArticleDto {
  id: SetupLearnArticleId;
  opened: boolean;
  path: string;
}

export type SetupDisplay = 'card' | 'collapsed' | 'hidden';

export interface SetupProgressDto {
  rows: SetupRowDto[];
  learn: { articles: SetupLearnArticleDto[]; openedCount: number };
  doneCount: number;
  /** The counted rows for this tenant: 8, or 7 for a custom-look studio. */
  total: number;
  display: SetupDisplay;
  showWelcome: boolean;
  cashOnly: boolean;
  /** Every counted row reads done right now (live). The API latches completion from this. */
  complete: boolean;
}

/** One mark (D4). The route body is the flat form of this union. */
export type SetupMark =
  | { kind: 'row-opened'; row: SetupOpenedRow }
  | { kind: 'article-opened'; articleId: SetupLearnArticleId }
  | { kind: 'cash-only' }
  | { kind: 'collapsed'; collapsed: boolean }
  | { kind: 'welcome-seen' };

export const SETUP_MARK_KINDS = [
  'row-opened',
  'article-opened',
  'cash-only',
  'collapsed',
  'welcome-seen',
] as const satisfies readonly SetupMark['kind'][];
export type SetupMarkKind = (typeof SETUP_MARK_KINDS)[number];

export function isSetupOpenedRow(value: unknown): value is SetupOpenedRow {
  return typeof value === 'string' && (SETUP_OPENED_ROWS as readonly string[]).includes(value);
}

export function isSetupLearnArticleId(value: unknown): value is SetupLearnArticleId {
  return typeof value === 'string' && (SETUP_LEARN_ARTICLE_IDS as readonly string[]).includes(value);
}

function notBlank(value: string | null): boolean {
  return value !== null && value.trim().length > 0;
}

function row(key: SetupRowKey, doneBy: SetupDoneBy | null, path: string | null): SetupRowDto {
  return { key, done: doneBy !== null, doneBy, path };
}

/** The counted rules, the count, the fold, the welcome and the live completion (D2, D5). */
export function computeSetupProgress(snapshot: SetupSnapshot): SetupProgressDto {
  const { marks } = snapshot;
  const openedRows = new Set(marks.openedRows);
  const openedArticles = new Set(marks.openedArticles);
  const opened = (r: SetupOpenedRow): SetupDoneBy | null => (openedRows.has(r) ? 'opened' : null);

  const articles: SetupLearnArticleDto[] = SETUP_LEARN_ARTICLE_IDS.map((id) => ({
    id,
    opened: openedArticles.has(id),
    path: helpArticlePath(id),
  }));
  const openedCount = articles.filter((a) => a.opened).length;

  const hoursDone =
    snapshot.defaultPractitionerId !== null &&
    snapshot.practitionersWithHours.includes(snapshot.defaultPractitionerId);
  const offeringDone = snapshot.hasLiveService || snapshot.hasScheduledEvent;
  const brandDone = notBlank(snapshot.brandLogo) && notBlank(snapshot.aboutStory) && notBlank(snapshot.aboutImageUrl);
  const paymentsDoneBy: SetupDoneBy | null = snapshot.stripeChargesEnabled
    ? 'data'
    : marks.cashOnly
      ? 'cash-only'
      : null;

  // Pick your look is done by a saved look only, never by opening the picker.
  // A custom-look studio bought its look from Telify: nothing to pick, so no
  // look row at all and one fewer to count.
  const rows: SetupRowDto[] = [
    row('hours', hoursDone ? 'data' : null, SETUP_PATHS.hours),
    row('offering', offeringDone ? 'data' : null, SETUP_PATHS.offering),
    row('form', opened('form'), setupFormPath(snapshot.registrationFormId)),
    row('brand', brandDone ? 'data' : null, SETUP_PATHS.brand),
    ...(snapshot.customLook ? [] : [row('look', snapshot.lookChosen ? 'data' : null, SETUP_LOOK_PICKER_PATH)]),
    row('refund', opened('refund'), SETUP_REFUND_PATH),
    row('payments', paymentsDoneBy, SETUP_PATHS.payments),
    row('learn', openedCount === SETUP_LEARN_ARTICLE_IDS.length ? 'opened' : null, null),
  ];

  const doneCount = rows.filter((r) => r.done).length;
  const complete = doneCount === rows.length;
  const display: SetupDisplay =
    marks.completed || complete ? 'hidden' : marks.collapsed ? 'collapsed' : 'card';

  return {
    rows,
    learn: { articles, openedCount },
    doneCount,
    total: rows.length,
    display,
    showWelcome: !marks.welcomeSeen && display !== 'hidden',
    cashOnly: marks.cashOnly,
    complete,
  };
}
